// GENERATED by helm-console/scripts/ensure-icons.mjs — DO NOT EDIT.
// Source of truth: helm-bridge/src/client/icons.ts
// (itself generated from lucide-static by helm-bridge/scripts/gen-icon-sprite.mjs).
// Regenerated on every console build; edits here are overwritten.
// The console and the release UI share ONE icon set on purpose — see the
// header of ensure-icons.mjs.
// The Bridge icon system. The `i-<id>` symbol sprite is generated from
// lucide-static by scripts/gen-icon-sprite.mjs (`npm run icons`) — the id→art
// mapping lives there (SPRITE_ICONS). Legacy nautical ids (helm, compass, …)
// and status glyphs (st-*) are preserved so call sites are unchanged; they now
// resolve to Lucide artwork. Contract is unchanged: 24-viewBox, pure stroke,
// `currentColor`; the consumer sets stroke-width via CSS (`.icon` default 1.7).
// Consumption: `<svg class="icon"><use href="#i-helm"/></svg>` (or `icon('helm')`).
// The sprite is injected once at boot (injectIconSprite from app.ts).
//
// PER-STYLE SETS (PR-5). A style may re-cut some of those ids in its own
// idiom — GLASS draws the optical family and the whole status ladder as
// aperture marks. The sprite is COMPOSED, not replaced: the Blueprint symbol
// table is the floor, the style's own glyphs override the ids it defines, and
// every id the style did not draw keeps the Blueprint artwork.
//
// A FALLBACK IS NO LONGER A SHIPPABLE STATE (owner correction 2026-09-07).
// The composition still exists — it is what keeps a slot from ever being
// EMPTY — but it is now a BUILD-TIME DIAGNOSTIC, not an accepted outcome:
// check-icon-refs.mjs refuses any style that leaves an id on the Blueprint
// glyph unless that style carries a dated COVERAGE_DEBT row, and the row
// ratchets down only. MAINFRAME is the proof of why: its stylesheet renders
// `.icon` fill-only, so the stroke-art fallbacks came out blank or as solid
// blobs in nine live nav slots, and every one of them "resolved" correctly.
// Composition guarantees a symbol; only coverage guarantees a GLYPH.
//
// The composed sprite is re-injected on a style switch. Under the identity
// style the composition is a no-op and `spriteForStyle()` returns the
// generated SPRITE string byte-for-byte, which is what makes the swap
// additive and provable by absence.
//
// THIS MODULE IS THE *EAGER* SEAT (portal weight, 2026-09-09). The primitives
// — spriteForStyle / injectIconSprite / icon / coverage — moved to
// `./icons-core.ts`, which imports no styled set at all. This file is what it
// always was: the REGISTRATION TABLE, which statically imports all seven sets
// and registers them at module scope, so importing `./icons.js` still means
// "every style's glyphs are available synchronously". The Helm does that on
// purpose (one long-lived session UI, style switched in place). The two help
// portals do NOT — they load one style's set on demand through
// `./icons-lazy.ts`, which is why the seven sets left portal.js. Measured
// there: 159,521 B of eager weight, 24.6 % of a 647,322 B bundle whose ceiling
// had 12,678 B of headroom left. See icons-core.ts for the full split note.

import { registerIconSet } from './icons-core.js';
import { GLASS_SYMBOLS } from './icons-glass.js';
import { SYNTHWAVE_SYMBOLS } from './icons-synthwave.js';
import { QUARTERDECK_SYMBOLS } from './icons-quarterdeck.js';
import { STARSHIP_SYMBOLS } from './icons-starship.js';
import { STUDIO_SYMBOLS } from './icons-studio.js';
import { MAINFRAME_SYMBOLS } from './icons-mainframe.js';
import { DESTROYER_SYMBOLS } from './icons-destroyer.js';

// The primitives, re-exported UNCHANGED so that every existing consumer —
// app.ts, the console's generated copy, check-icon-refs.mjs, style-assets-
// check.mjs and the dozen probes that bundle this file as their entry — keeps
// importing exactly what it imported before.
export {
  blueprintIconIds,
  icon,
  iconSetCoverage,
  injectIconSprite,
  registerIconSet,
  registeredIconSet,
  registeredIconSetIds,
  spriteForStyle,
} from './icons-core.js';

/** style id → the symbols that style re-cuts (id → symbol inner markup).
 * A style with no entry renders the Blueprint set unchanged.
 *
 * This table is also the REGISTRATION the icons gate parses: a set that
 * exists on disk and is not listed here is invisible to
 * scripts/check-icon-refs.mjs, and helm-console/scripts/ensure-icons.mjs
 * derives the files it must copy into the console bundle from these keys —
 * so adding a style set is one line here and one file beside this one.
 *
 * It is ALSO the table `./icons-lazy.ts` must match key-for-key: the lazy
 * loader cannot derive its import specifiers from this object (esbuild can
 * only split a dynamic import whose path is a literal), so the two registers
 * are held together by a gate instead — check-icon-refs.mjs refuses a style
 * that is in one and not the other, by name. */
export const STYLE_ICON_SETS: Record<string, Record<string, string>> = {
  glass: GLASS_SYMBOLS,
  synthwave: SYNTHWAVE_SYMBOLS,
  quarterdeck: QUARTERDECK_SYMBOLS,
  starship: STARSHIP_SYMBOLS,
  studio: STUDIO_SYMBOLS,
  mainframe: MAINFRAME_SYMBOLS,
  destroyer: DESTROYER_SYMBOLS,
};

// Importing this module IS the registration — the behaviour every consumer of
// `./icons.js` had before the split, preserved as a module-scope side effect
// so no call site had to learn a new step.
for (const [id, symbols] of Object.entries(STYLE_ICON_SETS)) registerIconSet(id, symbols);
